import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import PhoneNumber from "../models/PhoneNumber";
import HospitalUtils from "../classes/HospitalUtils";
import Person from "../models/Person";
import TextView from "./TextView";
import ResponseRelativesView from "./ResponseRelativesView";
import SearchRequest from "../models/SearchRequest";
import ReadTextView from "./ReadTextView";
import RequestStatus from "../classes/RequestStatus";
import DateUtils from "../classes/DateUtils";
import { BuildContext } from "../classes/BuildContext";

export interface RequestResponseViewProps extends BaseUIProps {
  key?: string;
  obj: SearchRequest;
}

class _RequestResponseViewState extends ObservableComponent<RequestResponseViewProps> {
  static defaultProps = { obj: null };
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: RequestResponseViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): SearchRequest {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "obj",
        "obj.addressLine1",
        "obj.addressLine2",
        "obj.city",
        "obj.dateOfBirth",
        "obj.failedMessage",
        "obj.firstName",
        "obj.isAdvancedSearch",
        "obj.isBilled",
        "obj.lastName",
        "obj.middleName",
        "obj.mobile",
        "obj.notes",
        "obj.requestDate",
        "obj.response",
        "obj.response.dateOfBirth",
        "obj.response.emails",
        "obj.response.fullName",
        "obj.response.phoneNumbers",
        "obj.response.phoneNumbers.location",
        "obj.response.phoneNumbers.phoneNumber",
        "obj.response.phoneNumbers.phoneType",
        "obj.response.relatives",
        "obj.sSN",
        "obj.searchId",
        "obj.state",
        "obj.status",
        "obj.tahoeId",
        "obj.zip",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: RequestResponseViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                TextView({
                  data: "Request",
                  style: new ui.TextStyle({
                    fontSize: 18,
                    fontWeight: ui.FontWeight.w600,
                  }),
                  className: "x24a",
                  key: "0",
                }),
              ],
              className: "xe5d hc h",
              key: "0",
            }),
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Column({
                  mainAxisAlignment: ui.MainAxisAlignment.start,
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    ReadTextView({
                      title: "Name",
                      value:
                        this.obj?.firstName +
                          " " +
                          (this.obj?.middleName ?? "") +
                          "" +
                          this.obj.lastName ?? "",
                      key: "0",
                    }),
                    ReadTextView({
                      title: "Date Of Birth",
                      value: this.obj?.dateOfBirth.toString() ?? "",
                      key: "1",
                    }),
                    this.obj.isAdvancedSearch
                      ? ui.Column({
                          mainAxisAlignment: ui.MainAxisAlignment.start,
                          crossAxisAlignment: ui.CrossAxisAlignment.start,
                          children: [
                            ReadTextView({
                              title: "Contact Number",
                              value: this.obj?.mobile ?? "",
                              key: "0",
                            }),
                            ReadTextView({
                              title: "SSN",
                              value: HospitalUtils.getMaskedSSN(this.obj.sSN),
                              key: "1",
                            }),
                            ReadTextView({
                              title: "Is Billed",
                              value: this.obj?.isBilled.toString() ?? "",
                              key: "2",
                            }),
                            ReadTextView({
                              title: "Tahoe Id",
                              value: this.obj?.tahoeId.toString() ?? "",
                              key: "3",
                            }),
                          ],
                        })
                      : [],
                    ReadTextView({
                      title: "Note",
                      value: this.obj?.notes ?? "",
                      key: "3",
                    }),
                  ],
                  className: "x91d7",
                  key: "0",
                }),
                ui.Column({
                  mainAxisAlignment: ui.MainAxisAlignment.start,
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    ReadTextView({
                      title: "Request Id",
                      value: this.obj.searchId,
                      key: "0",
                    }),
                    ReadTextView({
                      title: "Request Date",
                      value: DateUtils.format(this.obj.requestDate),
                      key: "1",
                    }),
                    this.obj.isAdvancedSearch
                      ? ui.Column({
                          mainAxisAlignment: ui.MainAxisAlignment.start,
                          crossAxisAlignment: ui.CrossAxisAlignment.start,
                          children: [
                            ReadTextView({
                              title: "Address Line1",
                              value: this.obj?.addressLine1 ?? "",
                              key: "0",
                            }),
                            ReadTextView({
                              title: "Address Line2",
                              value: this.obj?.addressLine2 ?? "",
                              key: "1",
                            }),
                            ReadTextView({
                              title: "City",
                              value: this.obj?.city ?? "",
                              key: "2",
                            }),
                            ReadTextView({
                              title: "State",
                              value: this.obj?.state ?? "",
                              key: "3",
                            }),
                            ReadTextView({
                              title: "Zip",
                              value: this.obj?.zip ?? "",
                              key: "4",
                            }),
                          ],
                        })
                      : [],
                  ],
                  className: "xf05",
                  key: "1",
                }),
              ],
              className: "x4f1 hc h",
              key: "1",
            }),
          ],
          className: "x0ef hc h",
          key: "0",
        }),
        this.obj.status === RequestStatus.Failed
          ? ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                TextView({
                  data: this.obj.failedMessage,
                  softWrap: true,
                  style: new ui.TextStyle({ fontSize: 15, color: cStyle.c25 }),
                  className: "xbd0 hc",
                  key: "0",
                }),
              ],
              className: "xd2c hc h",
            })
          : [],
        this.obj.status === RequestStatus.Resolved
          ? TextView({
              data: "Response",
              style: new ui.TextStyle({
                fontSize: 18,
                fontWeight: ui.FontWeight.w600,
              }),
              className: "x0cf hc",
            })
          : [],
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            this.obj.status === RequestStatus.Resolved
              ? this.obj.response.isNotEmpty
                ? this.obj.response.expand((item) => [
                    ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.start,
                      crossAxisAlignment: ui.CrossAxisAlignment.start,
                      children: [
                        TextView({
                          data:
                            this.obj.response.length.toString() +
                            " Persons Found",
                          style: new ui.TextStyle({
                            fontSize: 18,
                            fontWeight: ui.FontWeight.w600,
                          }),
                          className: "x003 hc",
                          key: "0",
                        }),
                        ui.Column({
                          mainAxisAlignment: ui.MainAxisAlignment.start,
                          crossAxisAlignment: ui.CrossAxisAlignment.start,
                          children: [
                            ReadTextView({
                              title: "Name",
                              value: item?.fullName ?? "",
                              className: "x5a0 hc h",
                              key: "0",
                            }),
                            ReadTextView({
                              title: "Date Of  Birth",
                              value: item?.dateOfBirth ?? "",
                              className: "xa1 hc h",
                              key: "1",
                            }),
                          ],
                          className: "x962 hc h",
                          key: "1",
                        }),
                        ui.Row({
                          mainAxisAlignment: ui.MainAxisAlignment.start,
                          crossAxisAlignment: ui.CrossAxisAlignment.start,
                          children: [
                            ui.Column({
                              mainAxisAlignment: ui.MainAxisAlignment.start,
                              crossAxisAlignment: ui.CrossAxisAlignment.start,
                              children: [
                                ui.Table({
                                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                                  children: [
                                    ui.TableRow({
                                      children: [
                                        ui.TableCell({
                                          verticalAlignment:
                                            ui.TableCellVerticalAlignment
                                              .middle,
                                          child: TextView({
                                            data: "Phone Number",
                                            style: new ui.TextStyle({
                                              fontFamily:
                                                cStyle.tTextViewTableHeaderFontFamilyOn,
                                              color:
                                                cStyle.tTextViewTableHeaderColorOn,
                                              fontSize:
                                                cStyle.tTextViewTableHeaderFontSizeOn,
                                              fontWeight:
                                                cStyle.tTextViewTableHeaderFontWeightOn,
                                            }),
                                            className: "hc",
                                          }),
                                          className: "hc",
                                          key: "0",
                                        }),
                                        ui.TableCell({
                                          verticalAlignment:
                                            ui.TableCellVerticalAlignment
                                              .middle,
                                          child: TextView({
                                            data: "Phone Type",
                                            style: new ui.TextStyle({
                                              fontFamily:
                                                cStyle.tTextViewTableHeaderFontFamilyOn,
                                              color:
                                                cStyle.tTextViewTableHeaderColorOn,
                                              fontSize:
                                                cStyle.tTextViewTableHeaderFontSizeOn,
                                              fontWeight:
                                                cStyle.tTextViewTableHeaderFontWeightOn,
                                            }),
                                            className: "hc",
                                          }),
                                          className: "hc",
                                          key: "1",
                                        }),
                                        ui.TableCell({
                                          verticalAlignment:
                                            ui.TableCellVerticalAlignment
                                              .middle,
                                          child: TextView({
                                            data: "Location",
                                            style: new ui.TextStyle({
                                              fontFamily:
                                                cStyle.tTextViewTableHeaderFontFamilyOn,
                                              color:
                                                cStyle.tTextViewTableHeaderColorOn,
                                              fontSize:
                                                cStyle.tTextViewTableHeaderFontSizeOn,
                                              fontWeight:
                                                cStyle.tTextViewTableHeaderFontWeightOn,
                                            }),
                                            className: "hc",
                                          }),
                                          className: "hc",
                                          key: "2",
                                        }),
                                      ],
                                      className: "x548 hc",
                                      key: "0",
                                    }),
                                    item.phoneNumbers.isNotEmpty
                                      ? item.phoneNumbers.expand((forItem) => [
                                          ui.TableRow({
                                            children: [
                                              ui.TableCell({
                                                verticalAlignment:
                                                  ui.TableCellVerticalAlignment
                                                    .middle,
                                                child: TextView({
                                                  data: forItem.phoneNumber,
                                                  softWrap:
                                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                                  style: new ui.TextStyle({
                                                    fontFamily:
                                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                                    color:
                                                      cStyle.tTextViewTableCellTextColorOn,
                                                    fontSize:
                                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                                  }),
                                                  className: "hc",
                                                }),
                                                className: "hc",
                                                key: "0",
                                              }),
                                              ui.TableCell({
                                                verticalAlignment:
                                                  ui.TableCellVerticalAlignment
                                                    .middle,
                                                child: TextView({
                                                  data: forItem.phoneType,
                                                  softWrap:
                                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                                  style: new ui.TextStyle({
                                                    fontFamily:
                                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                                    color:
                                                      cStyle.tTextViewTableCellTextColorOn,
                                                    fontSize:
                                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                                  }),
                                                  className: "hc",
                                                }),
                                                className: "hc",
                                                key: "1",
                                              }),
                                              ui.TableCell({
                                                verticalAlignment:
                                                  ui.TableCellVerticalAlignment
                                                    .middle,
                                                child: TextView({
                                                  data: forItem.location,
                                                  softWrap:
                                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                                  style: new ui.TextStyle({
                                                    fontFamily:
                                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                                    color:
                                                      cStyle.tTextViewTableCellTextColorOn,
                                                    fontSize:
                                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                                  }),
                                                  className: "hc",
                                                }),
                                                className: "hc",
                                                key: "2",
                                              }),
                                            ],
                                            className: "x846 hc",
                                            key: forItem?.ident,
                                          }),
                                        ])
                                      : [],
                                  ],
                                  className: "xa17 hc h",
                                  key: "0",
                                }),
                                item.phoneNumbers.isEmpty
                                  ? ui.Column({
                                      mainAxisAlignment:
                                        ui.MainAxisAlignment.center,
                                      children: [
                                        TextView({
                                          data: "No Phone Numbers Found",
                                          style: new ui.TextStyle({
                                            fontWeight: ui.FontWeight.bold,
                                            fontSize: 16,
                                          }),
                                          className: "x049 hc",
                                          key: "0",
                                        }),
                                      ],
                                      className: "x689 hc",
                                    })
                                  : [],
                              ],
                              className: "x0d53 hc vc h",
                              key: "0",
                            }),
                            ui.Column({
                              mainAxisAlignment: ui.MainAxisAlignment.start,
                              crossAxisAlignment: ui.CrossAxisAlignment.start,
                              children: [
                                ui.Table({
                                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                                  children: [
                                    ui.TableRow({
                                      children: [
                                        ui.TableCell({
                                          verticalAlignment:
                                            ui.TableCellVerticalAlignment
                                              .middle,
                                          child: TextView({
                                            data: "Email",
                                            style: new ui.TextStyle({
                                              fontFamily:
                                                cStyle.tTextViewTableHeaderFontFamilyOn,
                                              color:
                                                cStyle.tTextViewTableHeaderColorOn,
                                              fontSize:
                                                cStyle.tTextViewTableHeaderFontSizeOn,
                                              fontWeight:
                                                cStyle.tTextViewTableHeaderFontWeightOn,
                                            }),
                                            className: "hc",
                                          }),
                                          className: "hc",
                                          key: "0",
                                        }),
                                      ],
                                      className: "x60d hc",
                                      key: "0",
                                    }),
                                    item.emails.isNotEmpty
                                      ? item.emails.expand((forItem) => [
                                          ui.TableRow({
                                            children: [
                                              ui.TableCell({
                                                verticalAlignment:
                                                  ui.TableCellVerticalAlignment
                                                    .middle,
                                                child: TextView({
                                                  data: forItem,
                                                  softWrap:
                                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                                  style: new ui.TextStyle({
                                                    fontFamily:
                                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                                    color:
                                                      cStyle.tTextViewTableCellTextColorOn,
                                                    fontSize:
                                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                                  }),
                                                  className: "hc",
                                                }),
                                                className: "hc",
                                                key: "0",
                                              }),
                                            ],
                                            className: "x81b hc",
                                            key: forItem?.toString(),
                                          }),
                                        ])
                                      : [],
                                  ],
                                  className: "xb82 hc h",
                                  key: "0",
                                }),
                                item.emails.isEmpty
                                  ? ui.Column({
                                      mainAxisAlignment:
                                        ui.MainAxisAlignment.center,
                                      children: [
                                        TextView({
                                          data: "No Emails found",
                                          style: new ui.TextStyle({
                                            fontWeight: ui.FontWeight.bold,
                                            fontSize: 16,
                                          }),
                                          className: "xea5 hc",
                                          key: "0",
                                        }),
                                      ],
                                      className: "x60d8 hc",
                                    })
                                  : [],
                              ],
                              className: "xa00 hc vc h",
                              key: "1",
                            }),
                          ],
                          className: "x19f hc h",
                          key: "2",
                        }),
                        ResponseRelativesView({
                          obj: item.relatives,
                          className: "x055 hc h",
                          key: "3",
                        }),
                      ],
                      className: "x79 hc h",
                      key: item?.ident,
                    }),
                  ])
                : TextView({
                    data: "No Response Found",
                    style: new ui.TextStyle({
                      fontSize: 18,
                      fontWeight: ui.FontWeight.w600,
                    }),
                    className: "x0b1 hc",
                  })
              : [],
          ],
          className: "x3b3 hc vc h",
          key: "3",
        }),
      ],
      className: ui.join(
        this.props.className,
        "RequestResponseView x2b7a hc h"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
}
export default function RequestResponseView(props: RequestResponseViewProps) {
  return React.createElement(_RequestResponseViewState, {
    ..._RequestResponseViewState.defaultProps,
    ...props,
  });
}
